import React, { useCallback, useEffect, useState } from 'react';
import { currentAvailableHotelFiltersVar, useFilterHotels, useGlobalContext, useHotelSearchResults, useRecordTiming, useSearch, useHotelSortResults, TripType, SortCriterion, useRenderCount, AnywhereSearchResultEnum, TimingType, FilterProductEnum, hotelSearchResultsMetric, } from '@honeycomb/data';
import { searchCreatedRouting, filtersUpdatedRouting, sortUpdatedRouting, FunnelStep } from '@honeycomb/routing';
import { useBreakpoints, FlexGrid2, Container, Dialog, ModalContent, Picture, Styler } from '@honeycomb/ui-core';
import { pushFilters, pushHotelSearchResults, pushSearchCriteria, storeSponsoredListings, trackSponsoredListingImpressions, createSearchObserver, disconnectSearchObserver, reportSearchTiming, pushFilterResetClick, trackNavl, } from '@honeycomb/tracking';
import { HotelSearchResults } from '../features/hotel-search-results/HotelSearchResults';
import { Search } from '../features/search/Search';
import { SortResults } from '../features/sort/SortResults';
import { HotelSearchResultsHeading } from '../features/hotel-search-results/HotelSearchResultsHeading/HotelSearchResultsHeading';
import { HotelSearchResultsMap } from '../features/hotel-search-results/HotelSearchResultsMap/HotelSearchResultsMap';
import { Filters } from '../features/filters/Filters';
import { NightsAdjustedNotification } from '../features/hotel/NightsAdjustedNotification/NightsAdjustedNotification';
import { PayPalPayInThreeMessage } from '../features/booking/payment/PayPalPayInThreeMessage';
import { StopSellMessageBlock } from '../features/hotel-search-results/StopSell/StopSellMessageBlock';
import { RobotDirectives } from '../features/head';
import { parseBoolean } from '../utils/string-utils';
import { FunnelStepper } from '../features/stepper/FunnelStepper';
import { FiltersModal } from '../features/filters/FiltersModal';
import { useShowPayPalPayInThreeMessage } from '../features/hotel/utils/useShowPayPalPayInThreeMessage';
// The idea with this was to pass in the option props to query so a brand could easily configure it
// I pretty sure that has not been done in the rest of the app :/
// Just move it to the hook?
export const HOTEL_SEARCH_RESULTS_QUERY = `
hotels {
    numberOfNights
    hotel {
        customerFeedback {
            overallRating
            numberOfReviews
            overallRatingDescription
        }
        tags {
            text
            themeColor
        }
    }
    pricingSummary {
        discountTypes
        cityTaxFormatted
        wasPriceFormatted(format: $currencyFormat)
        isSponsored
        impressionUrl
        clickUrl
        conversionUrl
        deposit
        depositFormatted(format: $currencyFormat)
        callout {
            text
            themeColor
        }
        monthlyInstalment {
            isAvailable
            initialPayment
            initialPaymentFormatted(format: $currencyFormat)
            subsequentPayment
            subsequentPaymentFormatted(format: $currencyFormat)
            numberOfInstalments
        }
    }
}`;
export function HotelsPage({ contentRegionOne, hotelSearchResultsSlot }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const { largeUp, mediumUp } = useBreakpoints();
    const [searchExpanded, setSearchExpanded] = useState(false);
    const { resourceStrings: { SPONSOREDHOTELSMESSAGE, HC_MARKETING_OVERLAY_TEXT, HC_MULTI_DEST_EXTENDEDFLIGHT_NOTIFICATION, }, configSettings: { HcHotelHolidayFilter, HcHotelSortOptions, HCDisableRobots, HcMarketOverlay }, } = useGlobalContext();
    const [hotelSearch, setSearch] = useSearch([searchCreatedRouting, (_) => currentAvailableHotelFiltersVar([])]);
    const { tripType, tripStateId } = hotelSearch;
    const [hotelSortResults, setHotelSortResults] = useHotelSortResults([sortUpdatedRouting]);
    const [hotelSearchResults, fetchNextHotels] = useHotelSearchResults([
        pushHotelSearchResults,
        trackSponsoredListingImpressions,
        storeSponsoredListings,
        reportSearchTiming,
        hotelSearchResultsMetric,
        (result) => {
            var _a;
            if (((_a = result.hotelSearchResults) === null || _a === void 0 ? void 0 : _a.hotelCount) === 0 && hotelSearch.destinations) {
                trackNavl(hotelSearch.destinations);
            }
        },
    ], HOTEL_SEARCH_RESULTS_QUERY, !(hotelSearch.tripType === TripType.Holiday || hotelSearch.tripType === TripType.Hotel));
    const [hotelFilters, setHotelFilters] = useFilterHotels([filtersUpdatedRouting, pushFilters]);
    const { data, error: searchError, loading: hotelsLoading, called: hotelsCalled, previousData } = hotelSearchResults;
    const loading = hotelsLoading || !hotelsCalled;
    const totalHotelCount = (_b = (_a = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _a === void 0 ? void 0 : _a.hotelCount) !== null && _b !== void 0 ? _b : null; // The total number of hotels in the result set.
    const filteredHotelCount = (_d = (_c = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _c === void 0 ? void 0 : _c.filteredHotelCount) !== null && _d !== void 0 ? _d : null; // The number of hotels that match the current filter settings.
    const withHotels = Boolean(totalHotelCount);
    const noResults = !loading && !withHotels;
    const isNumberOfNightsAdjusted = (_f = (_e = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _e === void 0 ? void 0 : _e.isNumberOfNightsAdjusted) !== null && _f !== void 0 ? _f : false;
    const isMultiDestination = ((_g = hotelSearch === null || hotelSearch === void 0 ? void 0 : hotelSearch.destinations) !== null && _g !== void 0 ? _g : []).length > 1 ||
        ((_h = hotelSearch === null || hotelSearch === void 0 ? void 0 : hotelSearch.destinations) !== null && _h !== void 0 ? _h : []).filter((d) => (d === null || d === void 0 ? void 0 : d.type) === AnywhereSearchResultEnum.TYPE).length;
    const isFlexibleSearch = (_j = hotelSearch === null || hotelSearch === void 0 ? void 0 : hotelSearch.flexibleDays) !== null && _j !== void 0 ? _j : hotelSearch === null || hotelSearch === void 0 ? void 0 : hotelSearch.duration;
    const stopSellMessage = (_k = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _k === void 0 ? void 0 : _k.stopSellMessage;
    const showPayPalPayInThreeMessage = useShowPayPalPayInThreeMessage() && !stopSellMessage && withHotels;
    if (searchError !== undefined) {
        throw new Error(searchError.message);
    }
    const showFullSearch = (val) => setSearchExpanded(val);
    const sortCriterion = [
        SortCriterion.RecommendedHighestFirst,
        SortCriterion.PriceLowestFirst,
        SortCriterion.CustomerRatingHighestFirst,
        SortCriterion.StarRatingHighestFirst,
    ];
    const sortOptions = sortCriterion.filter((option) => {
        return HcHotelSortOptions.includes(option);
    });
    const enabledFilterCategories = HcHotelHolidayFilter.split('|');
    const filters = (React.createElement(Filters, { state: {
            data: hotelFilters,
            loading,
            enabledFilterCategories,
        }, actions: { updateFilters: setHotelFilters, onFilterReset: () => pushFilterResetClick(!filteredHotelCount) } }));
    useRenderCount('HotelsPage');
    const [recordTimingMutation] = useRecordTiming();
    useEffect(() => {
        const searchTimingLogger = (duration) => {
            recordTimingMutation({
                variables: {
                    duration: Math.round(duration),
                    timing: TimingType.SearchLeadTime,
                    tripStateId,
                },
            });
        };
        createSearchObserver(searchTimingLogger);
        return () => disconnectSearchObserver();
    }, [recordTimingMutation, tripStateId]);
    const handleSortChange = useCallback((sortOption) => {
        setHotelSortResults(sortOption);
    }, [setHotelSortResults]);
    return (React.createElement(React.Fragment, null,
        parseBoolean(HCDisableRobots) && React.createElement(RobotDirectives, { values: ['nofollow', 'noindex'] }),
        React.createElement(Styler, { pb: 20, mih: 500 },
            React.createElement(Search, { state: hotelSearch, action: setSearch, tracking: pushSearchCriteria, reduced: true, sticky: true, expanded: searchExpanded, onExpandSearch: setSearchExpanded }),
            isNumberOfNightsAdjusted && withHotels && (React.createElement(NightsAdjustedNotification, { message: isMultiDestination || isFlexibleSearch
                    ? HC_MULTI_DEST_EXTENDEDFLIGHT_NOTIFICATION
                    : undefined })),
            React.createElement(Container, null,
                mediumUp && (React.createElement(FunnelStepper, { funnelStep: FunnelStep.ChooseHotel, tripType: tripType || undefined, loading: loading })),
                React.createElement(Styler, { mt: 4, mb: 5 },
                    React.createElement(FlexGrid2, { container: true, spacing: 4 },
                        React.createElement(FlexGrid2, { xs: 12, l: 8 },
                            React.createElement(HotelSearchResultsHeading, { sponsoredHotelMessage: stopSellMessage ? '' : SPONSOREDHOTELSMESSAGE, filteredHotelCount: filteredHotelCount || 0, loading: loading || filteredHotelCount == null })),
                        showPayPalPayInThreeMessage && (React.createElement(FlexGrid2, { xs: 12, l: 4 },
                            React.createElement(PayPalPayInThreeMessage, { loading: loading }))))),
                !loading && stopSellMessage && (React.createElement(StopSellMessageBlock, { actions: { showFullSearch }, state: {
                        stopSellMessage,
                        tripType: tripType || TripType.Hotel,
                    } })),
                !stopSellMessage && (React.createElement(React.Fragment, null,
                    !largeUp && !noResults && (React.createElement(Styler, { my: 4 },
                        React.createElement(FiltersModal, { loading: loading, filteredDataCount: filteredHotelCount || 0, product: FilterProductEnum.HOTEL }, filters))),
                    React.createElement(FlexGrid2, { container: true, justifyContent: "space-between" },
                        largeUp && !noResults && (React.createElement(FlexGrid2, { item: true, xs: 3 },
                            React.createElement(Styler, { mr: { xs: 6, xl: 10 } },
                                filters,
                                contentRegionOne && React.createElement(Styler, { mt: 6 }, contentRegionOne)))),
                        React.createElement(FlexGrid2, { item: true, xs: "fill" },
                            (loading || (filteredHotelCount || 0) > 0) && (React.createElement(Styler, { mb: 5, display: "flex", justifyContent: "space-between" },
                                React.createElement(SortResults, { currentSortValue: hotelSortResults, sortValueOptions: sortOptions, onSortChange: handleSortChange, loading: loading }),
                                React.createElement(HotelSearchResultsMap, { hotels: ((_l = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _l === void 0 ? void 0 : _l.hotels) || undefined, loading: loading }))),
                            React.createElement(HotelSearchResults, { state: { hotelSearchResults, hotelSearch }, actions: {
                                    fetchNextHotels,
                                    setHotelFilters,
                                    showFullSearch,
                                }, hotelSearchResultsSlot: hotelSearchResultsSlot }))))),
                !largeUp && withHotels && contentRegionOne && React.createElement(Styler, { mt: 10 }, contentRegionOne),
                HcMarketOverlay && HC_MARKETING_OVERLAY_TEXT && loading && !previousData && (React.createElement(Dialog, { open: true, "aria-labelledby": "marketing-overlay", size: "s", BackdropProps: { shade: 'light' } },
                    React.createElement(ModalContent, { disableGutters: true },
                        React.createElement(Picture, { "data-id": "marketing-overlay", imageSet: {
                                s: HcMarketOverlay,
                                xs: HcMarketOverlay,
                            }, alt: HC_MARKETING_OVERLAY_TEXT, w: "100%", h: "auto" }))))))));
}
